import { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useSearchParams } from 'react-router-dom'
import { Layout, LayoutContent, LayoutHeader } from '@astryxdesign/core/Layout'
import { Stack } from '@astryxdesign/core/Stack'
import { Heading } from '@astryxdesign/core/Heading'
import { Text } from '@astryxdesign/core/Text'
import { Button } from '@astryxdesign/core/Button'
import { Selector } from '@astryxdesign/core/Selector'
import { TabList, Tab } from '@astryxdesign/core/TabList'
import CourtDay from './CourtDay'
import CourtMonth from './CourtMonth'
import CourtHours from './CourtHours'
import ClubMembers from './ClubMembers'
import CourtOccupancy from './CourtOccupancy'
import {
    chooseClub,
    fullName,
    isoDay,
    isoMonth,
    parseIsoDay,
    readConsoleRoute,
    readOccupancyRange,
    sessionEndText,
    todayParts,
} from './consoleText'

// The console once logged in: who is logged in, which club, a way to change
// club, a way out - and the club's courts, by day or by month, its
// opening hours and its members.
//
// Built from the blank template: a header and one content region. The
// views are tabs under the club's name rather than navigation, because they
// are all about the same club. Which tab and which date are in the address
// - "?vue=jour&date=2026-10-21", "?vue=mois&date=2026-10", "?vue=horaires",
// "?vue=adherents", "?vue=occupation&du=2026-10-01&au=2026-10-31" - so a
// reload, or a link sent to another admin, opens on the same place.
const CLUB_KEY = 'padelr.console.club'

function rememberedClub() {
    try {
        return window.localStorage.getItem(CLUB_KEY) || ''
    } catch (e) {
        return ''
    }
}

function rememberClub(id) {
    try {
        window.localStorage.setItem(CLUB_KEY, id)
    } catch (e) {
        // Storage refused: the choice lasts until the page is reloaded.
    }
}

export default function ConsoleHome({ me, onLogout, isLoggingOut, onSessionLost }) {
    const [clubId, setClubId] = useState(() => {
        const club = chooseClub(me.clubs, rememberedClub())
        return club ? club.id : ''
    })

    // The clubs are re-read whenever the page comes back into view; a club
    // the admin lost in the meantime drops out and the choice falls back.
    useEffect(() => {
        const club = chooseClub(me.clubs, clubId)
        if (club && club.id !== clubId) setClubId(club.id)
    }, [me.clubs, clubId])

    const club = chooseClub(me.clubs, clubId)

    const onClubChange = id => {
        setClubId(id)
        rememberClub(id)
    }

    const [params, setParams] = useSearchParams()
    const vue = params.get('vue')
    const date = params.get('date')
    const du = params.get('du')
    const au = params.get('au')
    // Memoised on the strings it is read from: the views reload whenever the
    // date they are given changes, and a fresh object on every render would
    // read as a change every time.
    const todayKey = isoDay(todayParts())
    const route = useMemo(
        () => readConsoleRoute(vue, date, parseIsoDay(todayKey)),
        [vue, date, todayKey],
    )
    const occupancyRange = useMemo(
        () => readOccupancyRange(du, au, parseIsoDay(todayKey)),
        [du, au, todayKey],
    )

    // Changing tab is a step worth going back from; stepping through the
    // days or months replaces the entry, or the back button would have to
    // walk back through every one of them.
    // The hours have no date; the day and the month come back from them to
    // today.
    const go = useCallback((nextView, nextDate, replace) => {
        setParams(nextDate ? { vue: nextView, date: nextDate } : { vue: nextView }, { replace })
    }, [setParams])

    const onTab = tab => {
        if (tab === route.view) return
        if (tab === 'horaires' || tab === 'adherents' || tab === 'occupation') go(tab, null, false)
        else if (tab === 'mois') go('mois', route.day ? isoMonth(route.day) : null, false)
        else go('jour', route.month ? isoMonth(route.month) : null, false)
    }

    const header = (
        <LayoutHeader>
            <Stack direction="horizontal" gap={4} vAlign="center" hAlign="between" wrap="wrap">
                <Stack gap={1}>
                    <Text type="supporting" color="secondary">Console Padelr</Text>
                    <Heading level={1}>{club ? club.name : ''}</Heading>
                    {club && club.ville && <Text color="secondary">{club.ville}</Text>}
                </Stack>
                <Stack direction="horizontal" gap={3} vAlign="center" wrap="wrap">
                    <Stack gap={0.5} hAlign="end">
                        <Text size="sm">{`Connecté en tant que ${fullName(me)}`}</Text>
                        <Text type="supporting" color="secondary">{sessionEndText(Number(me.expires_at))}</Text>
                    </Stack>
                    {me.clubs.length > 1 && (
                        <Selector
                            label="Club"
                            isLabelHidden
                            options={me.clubs.map(c => ({ value: c.id, label: c.name, description: c.ville }))}
                            value={club ? club.id : undefined}
                            onChange={onClubChange}
                        />
                    )}
                    <Button
                        label="Se déconnecter"
                        variant="secondary"
                        isLoading={isLoggingOut}
                        onClick={onLogout}
                    />
                </Stack>
            </Stack>
            <TabList value={route.view} onChange={onTab} hasDivider isFullBleed>
                <Tab value="jour" label="Jour" />
                <Tab value="mois" label="Mois" />
                <Tab value="occupation" label="Occupation" />
                <Tab value="horaires" label="Horaires" />
                <Tab value="adherents" label="Adhérents" />
            </TabList>
        </LayoutHeader>
    )

    // Keyed by the club: changing club starts each view from nothing rather
    // than showing one club's courts under another's name while it loads.
    let view = null
    if (club && route.view === 'occupation') {
        view = (
            <CourtOccupancy
                key={club.id}
                clubId={club.id}
                clubName={club.name}
                range={occupancyRange}
                onRangeChange={r => setParams({ vue: 'occupation', du: isoDay(r.from), au: isoDay(r.to) }, { replace: true })}
                onSessionLost={onSessionLost}
            />
        )
    } else if (club && route.view === 'adherents') {
        view = (
            <ClubMembers
                key={club.id}
                clubId={club.id}
                onSessionLost={onSessionLost}
            />
        )
    } else if (club && route.view === 'horaires') {
        view = (
            <CourtHours
                key={club.id}
                clubId={club.id}
                onOpenDay={day => go('jour', isoDay(day), false)}
                onSessionLost={onSessionLost}
            />
        )
    } else if (club && route.view === 'mois') {
        view = (
            <CourtMonth
                key={club.id}
                clubId={club.id}
                month={route.month}
                onMonthChange={month => go('mois', isoMonth(month), true)}
                onOpenDay={day => go('jour', isoDay(day), false)}
                onSessionLost={onSessionLost}
            />
        )
    } else if (club) {
        view = (
            <CourtDay
                key={club.id}
                clubId={club.id}
                date={route.day}
                onDateChange={day => go('jour', isoDay(day), true)}
                onOpenHours={() => go('horaires', null, false)}
                onSessionLost={onSessionLost}
            />
        )
    }

    return (
        <Layout padding={6} contentWidth={1280} height="auto" header={header}>
            <LayoutContent padding={6}>
                {view}
            </LayoutContent>
        </Layout>
    )
}

ConsoleHome.propTypes = {
    me: PropTypes.shape({
        first_name: PropTypes.string,
        last_name: PropTypes.string,
        clubs: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.string.isRequired,
            name: PropTypes.string,
            ville: PropTypes.string,
        })).isRequired,
        expires_at: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }).isRequired,
    onLogout: PropTypes.func.isRequired,
    isLoggingOut: PropTypes.bool,
    // Called when a console call answers 401: the session is gone.
    onSessionLost: PropTypes.func.isRequired,
}
